import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Hidden from "@material-ui/core/Hidden";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import TicketsManager from "../../components/TicketsManagerTabs/";
import Ticket from "../../components/Ticket/";
import logo from "../../assets/logo.png";

// Icons
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import SpeedIcon from "@material-ui/icons/Speed";
import MenuIcon from "@material-ui/icons/Menu";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const useStyles = makeStyles(theme => ({
  chatContainer: {
    flex: 1,
    padding: theme.spacing(2),
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      height: "100%",
    },
  },

  chatPaper: {
    display: "flex",
    height: "100%",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 16,
    },
  },

  gridContainer: {
    height: "100%",
  },

  contactsWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backgroundColor: theme.palette.type === "dark" ? "#12121a" : "#ffffff",
    [theme.breakpoints.down("sm")]: {
      borderRight: "none",
    },
  },

  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    backgroundColor: theme.palette.type === "dark" ? "#0d0d14" : "#f8fafc",
  },

  // Mobile drawer
  mobileDrawer: {
    width: "100%",
    maxWidth: 360,
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },

  mobileDrawerPaper: {
    width: "100%",
    maxWidth: 360,
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },

  // Mobile header
  mobileHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backgroundColor: theme.palette.type === "dark" ? "#12121a" : "#ffffff",
  },

  menuButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
  },

  mobileTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  // Welcome Screen
  welcomeScreen: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: theme.spacing(4),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #12121a 0%, #1a1a2e 100%)"
      : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },

  logoContainer: {
    marginBottom: theme.spacing(3),
    animation: "$float 3s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
    },
  },

  logoWrapper: {
    width: 100,
    height: 100,
    borderRadius: 24,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 16px 32px rgba(99, 102, 241, 0.3)",
    [theme.breakpoints.down("sm")]: {
      width: 80,
      height: 80,
      borderRadius: 20,
    },
  },

  logoImage: {
    width: 56,
    height: 56,
    filter: "brightness(0) invert(1)",
    [theme.breakpoints.down("sm")]: {
      width: 44,
      height: 44,
    },
  },

  welcomeTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: theme.spacing(1),
    letterSpacing: "-0.02em",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },

  welcomeSubtitle: {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    textAlign: "center",
    maxWidth: 350,
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem",
      marginBottom: theme.spacing(3),
      maxWidth: 280,
    },
  },

  featuresGrid: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1.5),
    },
  },

  featureCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2.5),
    borderRadius: 14,
    backgroundColor: theme.palette.type === "dark" 
      ? alpha("#ffffff", 0.05)
      : alpha("#6366F1", 0.05),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: "all 0.3s ease",
    cursor: "default",
    minWidth: 110,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      minWidth: 90,
    },
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.palette.type === "dark"
        ? "0 12px 24px rgba(0, 0, 0, 0.3)"
        : "0 12px 24px rgba(99, 102, 241, 0.15)",
      borderColor: theme.palette.primary.main,
    },
  },

  featureIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
    "& svg": {
      fontSize: 22,
      color: "#fff",
    },
    [theme.breakpoints.down("sm")]: {
      width: 38,
      height: 38,
      "& svg": {
        fontSize: 18,
      },
    },
  },

  featureIconPurple: {
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
  },

  featureIconPink: {
    background: "linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)",
  },

  featureIconCyan: {
    background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  },

  featureTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
    },
  },

  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-8px)",
    },
  },

  statusBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 16,
    backgroundColor: alpha("#22C55E", 0.1),
    border: `1px solid ${alpha("#22C55E", 0.2)}`,
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },

  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#22C55E",
    animation: "$pulse 2s infinite",
  },

  statusText: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#22C55E",
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
  },
}));

const TicketsCustom = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { ticketId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(!ticketId);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Quando seleciona um ticket no mobile, fecha o drawer
  React.useEffect(() => {
    if (ticketId && isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [ticketId, isMobile]);

  const ticketsList = (
    <Box className={classes.contactsWrapper} style={{ height: "100%" }}>
      <TicketsManager />
    </Box>
  );

  const welcomeScreen = (
    <Box className={classes.welcomeScreen}>
      <Box className={classes.logoContainer}>
        <Box className={classes.logoWrapper}>
          <img src={logo} alt="Omni" className={classes.logoImage} />
        </Box>
      </Box>

      <Typography className={classes.welcomeTitle}>
        Omni
      </Typography>
      <Typography className={classes.welcomeSubtitle}>
        {isMobile 
          ? "Toque no menu para ver suas conversas"
          : "Selecione uma conversa ao lado para começar a atender"
        }
      </Typography>

      <Box className={classes.featuresGrid}>
        <Box className={classes.featureCard}>
          <Box className={`${classes.featureIcon} ${classes.featureIconPurple}`}>
            <ChatBubbleOutlineIcon />
          </Box>
          <Typography className={classes.featureTitle}>
            Multicanal
          </Typography>
        </Box>

        <Box className={classes.featureCard}>
          <Box className={`${classes.featureIcon} ${classes.featureIconPink}`}>
            <HeadsetMicIcon />
          </Box>
          <Typography className={classes.featureTitle}>
            Atendimento
          </Typography>
        </Box>

        <Box className={classes.featureCard}>
          <Box className={`${classes.featureIcon} ${classes.featureIconCyan}`}>
            <SpeedIcon />
          </Box>
          <Typography className={classes.featureTitle}>
            Agilidade
          </Typography>
        </Box>
      </Box>

      <Box className={classes.statusBar}>
        <Box className={classes.statusDot} />
        <Typography className={classes.statusText}>
          Sistema online
        </Typography>
      </Box>
    </Box>
  );

  return (
    <div className={classes.chatContainer}>
      <div className={classes.chatPaper}>
        {/* Desktop Layout */}
        <Hidden smDown>
          <Grid container spacing={0} className={classes.gridContainer}>
            <Grid item md={5} lg={4} xl={3} className={classes.contactsWrapper}>
              <TicketsManager />
            </Grid>
            <Grid item md={7} lg={8} xl={9} className={classes.messagesWrapper}>
              {ticketId ? <Ticket /> : welcomeScreen}
            </Grid>
          </Grid>
        </Hidden>

        {/* Mobile Layout */}
        <Hidden mdUp>
          <Box style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Mobile Header */}
            {ticketId && (
              <Box className={classes.mobileHeader}>
                <IconButton 
                  className={classes.menuButton}
                  onClick={handleDrawerToggle}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.mobileTitle}>
                  Conversas
                </Typography>
              </Box>
            )}

            {/* Content */}
            <Box style={{ flex: 1, overflow: "hidden" }}>
              {ticketId ? (
                <Ticket />
              ) : (
                <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  {/* Mobile Menu Button when no ticket selected */}
                  <Box className={classes.mobileHeader}>
                    <IconButton 
                      className={classes.menuButton}
                      onClick={handleDrawerToggle}
                      size="small"
                    >
                      <MenuIcon />
                    </IconButton>
                    <Typography className={classes.mobileTitle}>
                      Conversas
                    </Typography>
                  </Box>
                  {welcomeScreen}
                </Box>
              )}
            </Box>

            {/* Mobile Drawer */}
            <Drawer
              variant="temporary"
              anchor="left"
              open={mobileDrawerOpen}
              onClose={handleDrawerToggle}
              classes={{
                paper: classes.mobileDrawerPaper,
              }}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {ticketsList}
            </Drawer>
          </Box>
        </Hidden>
      </div>
    </div>
  );
};

export default TicketsCustom;
